// @hooks/NTX/Organization/useGetOperatingUnitWithPermission.ts
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ntxAPI } from '@API/axiosNeatex';
 import { useAppSelector } from '@store/hooks';
import { resolveTenantId } from '@API/variables';

const CACHE_VERSION = 'v1';
const TTL_MS_DEFAULT = 6 * 60 * 60 * 1000; // 6 ساعات (عدّلها)

type OperatingUnitRow = {
  id: string;
  name?: string;
  name2?: string;
  // أي حقول أخرى موجودة عندك
};

type CacheShape = {
  fetchedAt: number;
  items: OperatingUnitRow[];
};

function safeJsonParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export default function useGetOperatingUnitWithPermission(opts?: { ttlMs?: number }) {
  const ttlMs = opts?.ttlMs ?? TTL_MS_DEFAULT;

  // لو عندك userId في الستور خلي المفتاح يتغير حسب المستخدم
  const userId = useAppSelector((s: any) => s?.auth?.user?.id || s?.user?.id || 'u0');
  const tenantId = (() => {
    try {
      return resolveTenantId() || 't0';
    } catch {
      return 't0';
    }
  })();

  const cacheKey = `ntx.operatingUnits.${CACHE_VERSION}.${tenantId}.${userId}`;

  const [data, setData] = useState<OperatingUnitRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<any>(null);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);

  const didInitRef = useRef(false);

  const loadCache = useCallback(() => {
    const cached = safeJsonParse<CacheShape>(localStorage.getItem(cacheKey));
    if (!cached?.items?.length) return null;

    const age = Date.now() - (cached.fetchedAt || 0);
    const isFresh = age <= ttlMs;

    return { cached, isFresh };
  }, [cacheKey, ttlMs]);

  const saveCache = useCallback(
    (items: OperatingUnitRow[]) => {
      const payload: CacheShape = { fetchedAt: Date.now(), items };
      localStorage.setItem(cacheKey, JSON.stringify(payload));
      setLastUpdated(payload.fetchedAt);
    },
    [cacheKey]
  );

  // ✅ هذا هو استدعاء الـ API الحقيقي (عدّل endpoint حسب مشروعك)
  const fetchFromApi = useCallback(async (): Promise<OperatingUnitRow[]> => {
    // مثال فقط — عدّل للمسار الحقيقي عندك:
    // const res = await ntxAPI.get('/NTX/Organization/GetOperatingUnitWithPermission');
    // return res.data ?? [];

    const res = await ntxAPI.get('Organization/GetOperatingUnitWithPermission');
    // debugger
    return (res?.data ?? []) as OperatingUnitRow[];
  }, []);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      const items = await fetchFromApi();
      setData(items);
      saveCache(items);
    } catch (e) {
      setError(e);
      // لا تمسح الداتا الحالية، خلي المستخدم يستفيد من الكاش
    } finally {
      setRefreshing(false);
    }
  }, [fetchFromApi, saveCache]);

  // ✅ init: اقرأ الكاش أولاً، ثم قرر هل تحتاج fetch
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    const c = loadCache();
    if (c?.cached?.items?.length) {
      setData(c.cached.items);
      setLastUpdated(c.cached.fetchedAt);
      setLoading(false);

      // إذا الكاش قديم، سو تحديث (لكن مرة واحدة فقط)
      if (!c.isFresh) {
        refresh();
      }
      return;
    }

    // ما فيه كاش: fetch أول مرة
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const items = await fetchFromApi();
        setData(items);
        saveCache(items);
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [loadCache, refresh, fetchFromApi, saveCache]);

  // ✅ Options جاهزة للـ Select (متوافق مع بقية الصفحات)
  const OperatingUnitOptions = useMemo(() => {
    return (data ?? []).map(x => ({
      value: x.id,
      label: x.name2 || x.name || x.id
    }));
  }, [data]);

  return {
    data,
    OperatingUnitOptions,
    loading,
    refreshing,
    refresh,
    error,
    lastUpdated
  };
}
